import { config } from './webconfig';

interface ValidationErrorBody {
  field: string;
  message: string;
  code: string;
}

export class ValidationError extends Error {
  constructor(public errors: ValidationErrorBody[]) {
    super('Validation Error');
  }
}

export class NotAuthenticatedError extends Error {
  constructor() {
    super('Not Authenticated');
  }
}

export class ApiClient {
  private readonly baseUrl: string = config.apiUrl;

  constructor(private readonly token: string | null = null) {
    //
  }

  async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const config: RequestInit = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(this.token && { Authorization: `Bearer ${this.token}` }),
        ...options.headers,
      },
      credentials: 'include',
    };

    const fetchResult = await fetch(`${this.baseUrl}${endpoint}`, config);
    const result = await fetchResult.json();
    if (fetchResult.ok) {
      return result;
    }

    if (fetchResult.status === 400) {
      throw new ValidationError(result);
    }

    if (fetchResult.status === 401) {
      throw new NotAuthenticatedError();
    }

    throw result;
  }

  get<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  post<T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  put<T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  delete<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}
